import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useAcknowledgePendingAlarmMutation,
  useGetAlarmsQuery,
  useSilencePendingAlarmMutation,
} from 'services/Alarms/Alarms.api';
import AlarmsHeader from 'features/Alarms/components/AlarmsHeader/AlarmsHeader';
import AlarmsAccordion from 'features/Alarms/components/AlarmsAccordion/AlarmsAccordion';
import ShareAlarmModal from '../../components/ShareAlarmModal';
import type { Alarm } from '../../types/Alarms.types';
import { buildAlarmQueryParams } from '../../utils/AlarmQueryBuilder';
import { isSilenced } from '../../utils/alarmUtils';
import AlarmsTable from 'features/Alarms/components/AlarmsTable';

export default function PendingAlarms() {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareAlarmData, setShareAlarmData] = useState<Alarm | null>(null);

  const [acknowledgePendingAlarm] = useAcknowledgePendingAlarmMutation();
  const [silencePendingAlarm] = useSilencePendingAlarmMutation();

  // Handler functions for alarm actions
  const handleAcknowledge = async (alarm: Alarm): Promise<boolean> => {
    try {
      const result = await acknowledgePendingAlarm(alarm.id).unwrap();
      if (result?.meta?.updated) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error('Acknowledge failed', err);
      return false;
    }
  };

  const handleSilence = async (alarm: Alarm): Promise<boolean> => {
    if (isSilenced(alarm)) return false;
    try {
      const result = await silencePendingAlarm({ id: alarm.id, silence: true }).unwrap();
      if (result?.meta?.updated) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error('Silence failed', err);
      return false;
    }
  };

  const handleUnsilence = async (alarm: Alarm): Promise<boolean> => {
    if (!isSilenced(alarm)) return false;
    try {
      const result = await silencePendingAlarm({ id: alarm.id, silence: false }).unwrap();
      if (result?.meta?.updated) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error('Unsilence failed', err);
      return false;
    }
  };

  const params = useMemo(
    () => buildAlarmQueryParams(page + 1, limit, 1, search),
    [page, limit, search],
  );
  const { data: alarmQuery, isLoading, isFetching, error } = useGetAlarmsQuery(params);
  const alarmList = alarmQuery?.data || [];
  const totalCount = alarmQuery?.meta?.resultCount || 0;
  const maxPageIndex = Math.max(0, Math.ceil(totalCount / limit) - 1);
  const handlePageChanged = (newPage: number) => setPage(newPage);
  const handleLimitChanged = (newLimit: number) => {
    setLimit(newLimit);
    setPage(0);
  };
  const handleSearchChanged = (newSearch: string) => {
    setSearch(newSearch);
    setPage(0);
  };
  const allSelected = alarmList?.length > 0 && selectedRowIds.length === alarmList.length;
  const onSelectAll = () => {
    if (!alarmList?.length) return;
    if (selectedRowIds.length !== alarmList.length) {
      setSelectedRowIds(alarmList.map((alarm: Alarm) => alarm.id));
    } else {
      setSelectedRowIds([]);
    }
  };

  const onAcknowledge = () => {
    if (selectedRowIds.length > 1) {
      let successCount = 0;
      selectedRowIds.forEach((id) => {
        const alarm = alarmList.find((a: Alarm) => a.id === id);
        if (alarm) {
          handleAcknowledge(alarm);
          successCount++;
        }
      });
      toast.success(`Acknowledged ${successCount} alarms`);
    } else if (selectedRowIds.length === 1) {
      const alarm = alarmList.find((a: Alarm) => a.id === selectedRowIds[0]);
      if (alarm) {
        handleAcknowledge(alarm);
        toast.success(`Acknowledged ${alarm?.info?.type} alarm`);
      }
    }
    setSelectedRowIds([]);
  };

  const onSilence = () => {
    if (selectedRowIds.length > 1) {
      let successCount = 0;
      selectedRowIds.forEach((id) => {
        const alarm = alarmList.find((a: Alarm) => a.id === id);
        if (alarm && !isSilenced(alarm)) {
          handleSilence(alarm);
          successCount++;
        }
      });
      toast.success(`Silenced ${successCount} alarms`);
    } else if (selectedRowIds.length === 1) {
      const alarm = alarmList.find((a: Alarm) => a.id === selectedRowIds[0]);
      if (alarm && !isSilenced(alarm)) {
        handleSilence(alarm);
        toast.success(`Silenced ${alarm?.info?.type} alarm`);
      }
    }
    setSelectedRowIds([]);
  };

  const onUnsilence = () => {
    if (selectedRowIds.length > 1) {
      let successCount = 0;
      selectedRowIds.forEach((id) => {
        const alarm = alarmList.find((a: Alarm) => a.id === id);
        if (alarm && isSilenced(alarm)) {
          handleUnsilence(alarm);
          successCount++;
        }
      });
      toast.success(`Unsilenced ${successCount} alarms`);
    } else if (selectedRowIds.length === 1) {
      const alarm = alarmList.find((a: Alarm) => a.id === selectedRowIds[0]);
      if (alarm && isSilenced(alarm)) {
        handleUnsilence(alarm);
        toast.success(`Unsilenced ${alarm?.info?.type} alarm`);
      }
    }
    setSelectedRowIds([]);
  };
  const onShare = (alarm: Alarm) => {
    setShareAlarmData(alarm);
    setShareOpen(true);
  };

  return (
    <div className="d-flex flex-column h-100">
      <AlarmsHeader
        title="Pending Alarms"
        pageIndex={page}
        maxPageIndex={maxPageIndex}
        headerFields={[]}
        onPageChanged={handlePageChanged}
        limit={limit}
        onLimitChanged={handleLimitChanged}
        onSearchChanged={handleSearchChanged}
        silenceControlsEnabled
        allSelected={allSelected}
        onSelectAll={onSelectAll}
        onAcknowledge={onAcknowledge}
        onSilence={onSilence}
        onUnsilence={onUnsilence}
      />
      <div className="flex-grow-1 overflow-auto">
        {isLoading || isFetching ? (
          <div className="text-center py-5 text-secondary">Loading alarms…</div>
        ) : error ? (
          <div className="text-center py-5 text-danger">Failed to load alarms.</div>
        ) : !alarmList || alarmList.length === 0 ? (
          <div className="text-center py-5 text-muted">No alarms found.</div>
        ) : (
          <>
            <div className="d-none d-lg-block">
              <AlarmsTable
                alarms={alarmList}
                selectedRowIds={selectedRowIds}
                setSelectedRowIds={setSelectedRowIds}
                handleAcknowledge={handleAcknowledge}
                handleSilence={handleSilence}
                handleUnsilence={handleUnsilence}
                handleShare={onShare}
              />
            </div>
            <div className="d-block d-lg-none">
              <AlarmsAccordion
                alarms={alarmList}
                selectedRowIds={selectedRowIds}
                setSelectedRowIds={setSelectedRowIds}
                handleAcknowledge={handleAcknowledge}
                handleSilence={handleSilence}
                handleUnsilence={handleUnsilence}
                handleShare={onShare}
              />
            </div>
          </>
        )}
      </div>
      <ShareAlarmModal
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setShareAlarmData(null);
        }}
        alarm={shareAlarmData}
      />
    </div>
  );
}
